import React from "react";
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useParams } from "react-router-dom";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useGetProgramByIdQuery } from "../api/programsApi";

const ProgramDetailPage = () => {
  const { id } = useParams();
  const { data, isLoading, isError } = useGetProgramByIdQuery(id!);

  if (isLoading) {
    return (
      <Box
        height="70vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box
        height="70vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography color="error">Failed to load program details.</Typography>
      </Box>
    );
  }

  const program = data;
  const { name, description, created_at, objectives, achievements, image_url } =
    program;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* ✅ Program Image */}
        {image_url && (
          <Box
            sx={{
              width: "100%",
              mb: 3,
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: 2,
            }}
          >
            <img
              src={image_url}
              alt={name || "Program Image"}
              style={{
                width: "100%",
                height: "auto",
                maxHeight: 400,
                objectFit: "cover",
                borderRadius: "8px",
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "https://via.placeholder.com/800x400?text=No+Image";
              }}
            />
          </Box>
        )}

        {/* Program name */}
        <Typography variant="h4" component="h1" gutterBottom>
          {name || "Untitled Program"}
        </Typography>

        {/* Created date */}
        {created_at && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <CalendarMonthIcon color="action" />
            <Typography variant="body2" color="text.secondary">
              {new Date(created_at).toLocaleDateString()}
            </Typography>
          </Box>
        )}

        {/* Overview */}
        {description && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Overview of the Pillar
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {description}
            </Typography>
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Objectives */}
          {objectives && objectives.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    What the Pillar Aims to Achieve
                  </Typography>
                  <List dense>
                    {objectives.map((objective: any, index: number) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${index + 1}. ${
                            objective.program_objective
                          }`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Achievements */}
          {achievements && achievements.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="success.main">
                    Result Highlight (Key Outcomes)
                  </Typography>
                  <List dense>
                    {achievements.map((achievement: any, index: number) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${index + 1}. ${
                            achievement.program_achievement
                          }`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Fallback when no details */}
        {(!objectives || objectives.length === 0) &&
          (!achievements || achievements.length === 0) && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No additional details available
              </Typography>
            </Box>
          )}
      </Paper>
    </Container>
  );
};

export default ProgramDetailPage;
